/* eslint-disable react-hooks/exhaustive-deps */
import React, { useReducer, useMemo, useCallback, memo } from 'react';
import CatalogUpdateReferencesContext from './CatalogUpdateReferencesContext';
import initialCatalogUpdateReferencesState from './initialCatalogUpdateReferencesState';
import catalogUpdateReferencesReducer from 'contexts/catalogUpdateReferences/reducer/catalogUpdateReferences.reducer';
import * as action from 'contexts/catalogUpdateReferences/reducer/catalogUpdateReferences.actions';
import * as types from 'contexts/catalogUpdateReferences/reducer/catalogUpdateReferences.types';
import { useAuth0 } from '@auth0/auth0-react';
import { DropResult } from 'react-beautiful-dnd';
import { IChildrenProps } from 'interfaces/general.interface';
import useContextCompany from 'hooks/contexts/useContextCompany';
import {
  ISearchOnCatalogSearchTypes,
  ISearchOnCatalogTypes,
  IsetSelectedValuesProps,
  IupdateReferencesProps,
} from 'interfaces/catalogUpdateReferences.interface';
import { abortCatalogController } from 'utils/abortController';
import { useTranslation } from 'react-i18next';

export const catalogUpdateReferencesFiltersKeys = {
  brand: 'brand',
  color: 'color',
  season: 'season',
  division: 'division',
  family: 'family',
  gender: 'gender',
  segmentation: 'segmentation',
  price: 'price',
  reference: 'reference',
  material: 'material',
  bill: 'bill',
  order: 'order',
};

function CatalogUpdateReferencesProvider(props: IChildrenProps) {
  const [catalogUpdateReferencesState, dispatch] = useReducer(
    catalogUpdateReferencesReducer,
    initialCatalogUpdateReferencesState
  );
  const { getAccessTokenSilently } = useAuth0();
  const { company } = useContextCompany();
  const { t: translate } = useTranslation();

  const getCatalogItems = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.brand.id) return false;
    const signal = await abortCatalogController();
    return await action.getCatalogItemsAction({
      dispatch,
      catalogBodyFetch: catalogUpdateReferencesState.catalogBodyFetch,
      brandId: company?.brand.id,
      signal,
      token,
      translate,
    });
  }, [company?.brand.id]);

  const getFilters = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token || !company?.brand.id) return false;
    return await action.getFiltersAction({
      dispatch,
      brandId: company?.brand.id,
      token,
      translate,
    });
  }, [company?.brand.id]);

  const searchOnCatalogItems = useCallback(
    async ({
      searchField,
      searchText,
      searchType,
      type,
    }: {
      searchField: string;
      searchText: string | string[];
      searchType: ISearchOnCatalogSearchTypes;
      type: ISearchOnCatalogTypes;
    }) => {
      const token = await getAccessTokenSilently();
      if (!token || !company?.brand.id) return false;
      const signal = await abortCatalogController();
      return await action.searchOnCatalogItemsAction({
        dispatch,
        catalogBodyFetch: catalogUpdateReferencesState.catalogBodyFetch,
        searchField,
        searchText,
        searchType,
        type,
        signal,
        token,
        translate,
      });
    },
    [catalogUpdateReferencesState, company?.brand.id]
  );

  const setSelectedValues = useCallback((value: IsetSelectedValuesProps[]) => {
    dispatch({ type: types.SET_SELECTED_VALUES, payload: value });
  }, []);

  const updateReferences = useCallback(
    async ({ column, value }: IupdateReferencesProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.updateReferencesAction({
        dispatch,
        column,
        value,
        catalogBodyFetch: catalogUpdateReferencesState.catalogBodyFetch,
        selectedValues: catalogUpdateReferencesState.selectedValues,
        signal,
        token,
        translate,
      });
    },
    [
      catalogUpdateReferencesState.catalogBodyFetch,
      catalogUpdateReferencesState.selectedValues,
    ]
  );

  // Columns //////////////////
  const moveColumnsOrder = useCallback(
    (result: DropResult) => {
      return action.moveColumnsOrderAction({
        dispatch,
        result,
        columnsOrder: catalogUpdateReferencesState.columnsOrder,
      });
    },
    [catalogUpdateReferencesState.columnsOrder]
  );

  const checkColumn = useCallback(
    (columnKey: string) => {
      return action.checkColumnAction({
        dispatch,
        columnKey,
        columnsOrder: catalogUpdateReferencesState.columnsOrder,
      });
    },
    [catalogUpdateReferencesState.columnsOrder]
  );

  const handlePagination = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      const signal = await abortCatalogController();
      return await action.handlePaginationAction({
        dispatch,
        page,
        pageSize,
        catalogUpdateReferencesState,
        signal,
        token,
        translate,
      });
    },
    [catalogUpdateReferencesState]
  );

  const memoProvider = useMemo(
    () => ({
      ...catalogUpdateReferencesState,
      getCatalogItems,
      getFilters,
      searchOnCatalogItems,
      setSelectedValues,
      updateReferences,
      // Columns
      moveColumnsOrder,
      checkColumn,
      handlePagination,
    }),
    [
      catalogUpdateReferencesState,
      getCatalogItems,
      getFilters,
      searchOnCatalogItems,
      setSelectedValues,
      updateReferences,
      // Columns
      moveColumnsOrder,
      checkColumn,
      handlePagination,
    ]
  );

  return (
    <CatalogUpdateReferencesContext.Provider value={memoProvider}>
      {props.children}
    </CatalogUpdateReferencesContext.Provider>
  );
}

export default memo(CatalogUpdateReferencesProvider);
